import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Typography,
  Box,
  Alert,
  Paper,
  Avatar,
} from "@mui/material";
import {
  Person as PersonIcon,
} from "@mui/icons-material";
import { fetchUserCompaniesRequest } from "../store/slices/database/userCompaniesSlice";
import CustomPagination from "../components/common/CustomPagination";
import DataContainer from "../components/common/DataContainer/DataContainer";
import { useAuth } from "../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { setCompanyData, setCompany, clearCompany } from '../store/slices/database/selectedCompanySlice';
import { fetchAccessDataRequest, selectAccessDataMenu } from '../store/slices/database/accessDataSlice';
import { setProtectedMenuData } from '../store/slices/database/menuConfigSlice';
import Loader from "../components/common/custom-component/Loader";
import LoadingBackdrop from "../components/common/custom-component/LoadingBackdrop";
import { logger } from "helpers/logger";
import { fetchCompanyRolesThunk } from "../store/thunks/mdsThunks";
import { mapAuthzMenuItemsToNavItems } from "../helpers/menuItemMapper";
import { useTableColumns } from "../hooks/useTableColumns";

// Resolves the post-company-selection outcome from the resolved, already-visible-to-the-caller
// menu. landingPriority is a separate concern from sidebar display_order (see authz_menu_items'
// schema comment) - lowest non-null value wins among navigable items. Three distinct outcomes,
// each surfaced differently by the caller:
//   "ok"                 - a landingPriority-flagged item exists -> navigate there, no alert.
//   "no-landing-priority" - has navigable items, but none flagged -> stay put, warning alert.
//                            Not an error: the user has real access, just nothing configured to
//                            pick a default from - surfaced so it doesn't go unnoticed.
//   "no-access"           - zero navigable items at all -> stay put, error alert.
function resolveLanding(menuItems) {
  const navigable = (menuItems || []).filter((item) => item.to);
  if (navigable.length === 0) {
    return { status: "no-access" };
  }

  const withPriority = navigable.filter((item) => item.landingPriority != null);
  if (withPriority.length > 0) {
    const winner = [...withPriority].sort((a, b) => a.landingPriority - b.landingPriority)[0];
    return { status: "ok", to: winner.to };
  }

  return { status: "no-landing-priority" };
}

export default function UserCompaniesPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const keycloakAuth = useAuth();

  // userCompanies.data holds the whole ApiResponse<List<CompanyAccessDTO>> envelope (see
  // accessDataSlice's identical data?.data?.X pattern) - the actual array is nested one level
  // deeper, under .data.data, while loading/error live on the slice itself, not the envelope.
  const { data: companiesEnvelope, loading: companiesLoading, error: companiesError } = useSelector((state) => state.userCompanies || {});
  const userCompaniesData = companiesEnvelope?.data || [];

  // authz.* menu resolution: flat MenuItemAccessDTO list, visibility already cascaded down
  // the tree server-side. NavbarItem resolves `name` (a translation key) via t() itself, so
  // no translation lookup is needed here - just reshape field names. hasFetchedAccessData
  // (not array length) distinguishes "not fetched yet" from "fetched, genuinely zero items" -
  // the latter must NOT fall back to a stale cached menu from a previously selected company.
  const accessDataMenu = useSelector(selectAccessDataMenu);
  const hasFetchedAccessData = useSelector((state) => state.accessData.data != null);
  const userMenusFetched = useMemo(
    () => !hasFetchedAccessData ? undefined : mapAuthzMenuItemsToNavItems(accessDataMenu),
    [hasFetchedAccessData, accessDataMenu]
  );
  const userMenusCache = useSelector((state) => state.menuConfig.data?.protected);
  const userMenus = userMenusFetched ?? userMenusCache;

  const selectedCompany = useSelector((state) => state.selectedCompany.data?.companyData);

  const prevCompanyIdRef = useRef(selectedCompany?.companyId);
  const prevMenuRef = useRef(userMenus);
  const autoSelectCompanyRef = useRef(false);

  const [isLoadingBackdrop, setIsLoadingBackdrop] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  // null | "no-access" | "no-landing-priority" - see resolveLanding(). Deliberately never paired
  // with a navigate() call for either non-null value: staying on this page (no route change, no
  // remount) is what keeps the single-company auto-select guard (autoSelectCompanyRef) intact
  // and prevents a select/alert/redirect/remount/select... loop.
  const [accessIssue, setAccessIssue] = useState(null);

  const recordsPerPage = 10;
  const totalPages = Math.ceil(userCompaniesData.length / recordsPerPage);

  const user = keycloakAuth.user;
  const userId = user?.id;

  // Derived state
  const isBackgroundRefreshing = companiesLoading && userCompaniesData.length > 0;

  // Handles a resolveLanding() outcome uniformly. "ok" navigates. Anything else clears the
  // selection - both the Redux state (selectedCompany, which is redux-persist'd) AND
  // prevCompanyIdRef - rather than just showing the alert and leaving state as-is. Without this,
  // prevCompanyIdRef stays pointed at this company, so a later retry (a re-click, or the next
  // mount after a refresh, since selectedCompany survives to localStorage) sees the SAME
  // companyId and falls into the "already selected" branch instead of re-fetching - meaning a
  // server-side fix (e.g. re-enabling a menu item) would never be picked up without manually
  // clearing browser storage. Deliberately never calls navigate() for a non-"ok" outcome - see
  // the accessIssue state comment for why that matters.
  const applyLandingOutcome = useCallback((landing) => {
    if (landing.status === "ok") {
      setAccessIssue(null);
      navigate(landing.to);
      return;
    }

    dispatch(clearCompany());
    prevCompanyIdRef.current = null;
    setAccessIssue(landing.status);
  }, [dispatch, navigate]);

  const fetchCompanyRoles = useCallback(
    async (companyId) => {
      try {

        const companyRolesResponse = await dispatch(fetchCompanyRolesThunk({ companyId }));

        if (fetchCompanyRolesThunk.rejected.match(companyRolesResponse)) {
          logger.error("Error fetching company roles:", companyRolesResponse.payload?.data);
          return null;
        }

        const companyRoles = companyRolesResponse.payload?.data;
        //logger.info(companyRoles)
        return companyRoles;
      } catch (error) {
        logger.error("Error in fetchCompanyRoles:", error);
        return null;
      }
    },
    [dispatch]
  );



  /* -------------------------------- HANDLERS -------------------------------- */

  const handleSelectClick = useCallback(async (company) => {
    if (company?.companyId && company.companyId !== prevCompanyIdRef.current) {
      setAccessIssue(null);
      setIsLoadingBackdrop(true);
      const companyRolesRecs = await fetchCompanyRoles(company.companyId)
      logger.info("companyRolesRecs roles:", companyRolesRecs);
      // authz.* CompanyRoleDTO shape: {roleId, roleCode, displayName} - roleId (not a name
      // string) as value is more robust for whatever consumes this later (e.g. role assignment
      // needs to reference the role by id, not by a possibly-non-unique display string).
      const formattedRoles = companyRolesRecs.map(role => ({
        value: role.roleId,
        label: role.displayName || role.roleCode
      }));
      logger.info("Formatted roles:", formattedRoles);
      dispatch(setCompany({ companyData: company, companyRoles: formattedRoles }));
    } else {
      applyLandingOutcome(resolveLanding(userMenus));
    }
  }, [dispatch, fetchCompanyRoles, userMenus, applyLandingOutcome]);

  /* -------------------------------- EFFECTS -------------------------------- */

  useEffect(() => {
    // authz.* schema: identity comes from the token, no userId argument needed anymore.
    if (keycloakAuth.isAuthenticated && userId) {
      dispatch(fetchUserCompaniesRequest());
    }
  }, [dispatch, keycloakAuth.isAuthenticated, userId]);

  useEffect(() => {
    if (!selectedCompany?.companyId || !userId) return;

    if (selectedCompany.companyId !== prevCompanyIdRef.current) {
      prevCompanyIdRef.current = selectedCompany.companyId;
      // userId is no longer sent - AuthorizationController reads it from the validated token.
      dispatch(fetchAccessDataRequest({ companyId: selectedCompany.companyId }));
    }
  }, [dispatch, userId, selectedCompany?.companyId]);

  useEffect(() => {
    if (!autoSelectCompanyRef.current && userCompaniesData.length === 1) {
      autoSelectCompanyRef.current = true;
      handleSelectClick(userCompaniesData[0]);
    }
  }, [userCompaniesData, handleSelectClick]);

  useEffect(() => {
    if (userMenus && userMenus !== prevMenuRef.current) {
      prevMenuRef.current = userMenus;
      dispatch(setProtectedMenuData(userMenus));
      setIsLoadingBackdrop(false);
      applyLandingOutcome(resolveLanding(userMenus));
    }
  }, [userMenus, dispatch, applyLandingOutcome]);

  /* -------------------------------- DYNAMIC COLUMNS -------------------------------- */

  // Memoize actions and context to prevent unnecessary recalculations in useTableColumns
  const actions = useMemo(() => ({
    onSelect: handleSelectClick,
  }), [handleSelectClick]);

  const context = useMemo(() => ({
    selectedCompanyId: selectedCompany?.companyId,
  }), [selectedCompany?.companyId]);

  // Use the dynamic columns hook - fetches config from Supabase based on domain
  const { columns, isLoading: columnsLoading } = useTableColumns('UserCompanies', {
    actions,
    context,
  });

  /* -------------------------------- RENDER -------------------------------- */

  if (companiesLoading && userCompaniesData.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <Loader variant="circular" message="Fetching the companies that you belong to..." size="small" />
      </Box>
    );
  }

  if (companiesError) {
    return <Alert severity="error">{companiesError}</Alert>;
  }

  return (
    <Box>
      <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: "primary.main" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar sx={{ bgcolor: "primary.main" }}>
            <PersonIcon />
          </Avatar>
          <Typography variant="h6" sx={{ color: "primary.contrastText" }}>
            {user?.firstName || user?.username}'s Companies
          </Typography>
        </Box>
      </Paper>

      {accessIssue === "no-access" && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">
            You don't have any navigable privileges in this company.
          </Alert>
        </Box>
      )}

      {accessIssue === "no-landing-priority" && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="warning">
            No default landing page is configured for this company. Please contact your administrator.
          </Alert>
        </Box>
      )}

      <Box sx={{ padding: "1%" }}>
        {isBackgroundRefreshing && (
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Loader variant="circular" message="Loading..." size="small" />
          </Box>
        )}
        <DataContainer
          data={userCompaniesData}
          columns={columns} columnsLoading={columnsLoading}
          showRowNumbers
          stickyHeader
          rowKey="companyId"
          maxHeight={600}
        />
      </Box>

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
          <CustomPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(e, value) => setCurrentPage(value)}
          />
        </Box>
      )}

      <LoadingBackdrop
        open={isLoadingBackdrop}
        message="Loading data, please wait..."
        variant="bars"
        color="greenGradient"
      />
    </Box>
  );
}
